// Push-Auth-Frontend-Logik. Gerat-B-Seite (Login-Pfad) und
// Gerat-A-Seite (Approval-Banner) teilen sich diese Helpers.
//
// Gerat B:
//   - startPushLogin()        -> erzeugt einen Request, kriegt Code +
//                                 ExpiresAt, oeffnet SSE auf den Status
//                                 und resolved bei "approved" mit
//                                 setzenden Cookies via finalizePushLogin.
//
// Gerat A:
//   - approvePushRequest(id, code) durchlaeuft WebAuthn-Re-Auth +
//     Approve-Roundtrip.
//   - rejectPushRequest(id) markiert den Request als ablehnt und
//     bannt die Gerat-B-IP serverseitig.

import { postJSON } from "./api";

// PUSH_CODE_DIGITS spiegelt auth.PushCodeDigits aus dem Go-Backend wider.
// Eine Quelle, damit UI-Texte ("X-stelliger Code") und Format-/Validier-
// Helpers nicht auseinanderlaufen, wenn das Backend mal aendert.
export const PUSH_CODE_DIGITS = 6;

// formatPushCode rendert die rohen Ziffern als zwei moeglichst gleich
// grosse Gruppen mit Space dazwischen (z.B. "123 456" fuer 6 Stellen,
// "12 345" fuer 5). Symmetrisch zu LoginGate.svelte. Inputs mit
// Nicht-Ziffern werden defensiv normalisiert.
export function formatPushCode(raw: string): string {
  const digits = raw.replace(/\D+/g, "").slice(0, PUSH_CODE_DIGITS);
  if (digits.length <= Math.ceil(PUSH_CODE_DIGITS / 2)) return digits;
  const cut = Math.ceil(digits.length / 2);
  return digits.slice(0, cut) + " " + digits.slice(cut);
}

// base64url <-> ArrayBuffer Helper, lokal dupliziert (passkey.ts haelt
// die gleichen Funktionen privat). Spaeter zusammenfuehren wenn ein
// drittes Modul sie braucht.
function b64urlToBytes(s: string): ArrayBuffer {
  const pad = s.length % 4 === 0 ? "" : "=".repeat(4 - (s.length % 4));
  const std = (s + pad).replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(std);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
}

function bytesToB64url(b: ArrayBuffer): string {
  const bytes = new Uint8Array(b);
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export interface PushRequestResponse {
  ok: boolean;
  id?: number;
  code?: string;
  expires_at?: string;
  // retry_after_s wird vom Backend NUR im wrong_code-Cooldown-Fall
  // gesetzt; alle anderen ok=false-Pfade bleiben generisch. Der Wert
  // ist die Restwartezeit in Sekunden (>= 1).
  retry_after_s?: number;
}

export type PushStatus =
  | "pending"
  | "approved"
  | "rejected"
  | "wrong_code"
  | "expired";

// startPushLogin: Anfrage vom nicht-eingeloggten Browser an
// /push/request. Endpoint antwortet konstant in der Zeit; das FE muss
// darauf vorbereitet sein, dass ok=false ohne weitere Info kommt.
export async function startPushLogin(): Promise<PushRequestResponse> {
  return postJSON<PushRequestResponse>("/v2-api/auth/push/request");
}

// streamPushStatus oeffnet einen SSE-Stream und ruft den Callback bei
// jedem "status"-Event. Liefert eine cleanup-Funktion, die den Stream
// schliesst.
export function streamPushStatus(
  requestID: number,
  onStatus: (s: PushStatus) => void,
): () => void {
  const es = new EventSource(
    `/v2-api/auth/push/status/${encodeURIComponent(requestID)}`,
    { withCredentials: true },
  );
  es.addEventListener("status", (ev) => {
    onStatus((ev as MessageEvent).data as PushStatus);
  });
  es.onerror = () => {
    // Browser reconnected automatisch; bei finaler Abbruch faellt das
    // Promise zurueck auf den letzten gemeldeten Status. Wir schliessen
    // den Stream nicht selbst -- der Caller entscheidet.
  };
  return () => es.close();
}

// finalizePushLogin wird vom FE auf Gerat B aufgerufen, sobald der
// SSE-Stream "approved" gemeldet hat. Setzt Session + CSRF-Cookies.
export async function finalizePushLogin(requestID: number): Promise<void> {
  await postJSON<{ ok: true }>("/v2-api/auth/push/finalize", {
    request_id: requestID,
  });
}

// approvePushRequest: zwei Schritte. Erst /approve/begin holt die
// WebAuthn-Challenge, der Browser ruft navigator.credentials.get,
// dann /approve mit Assertion + Code.
export async function approvePushRequest(requestID: number, code: string): Promise<void> {
  const begin = await postJSON<{ publicKey: PublicKeyCredentialRequestOptionsJSON }>(
    "/v2-api/auth/push/approve/begin",
  );
  const opts = decodeRequestOptions(begin.publicKey);
  const cred = (await navigator.credentials.get({
    publicKey: opts,
  })) as PublicKeyCredential | null;
  if (!cred) throw new Error("Keine WebAuthn-Antwort");
  const assertion = encodeAssertion(cred);
  await postJSON<{ ok: true }>("/v2-api/auth/push/approve", {
    request_id: requestID,
    code,
    assertion,
  });
}

export async function rejectPushRequest(requestID: number): Promise<void> {
  await postJSON<{ ok: true }>("/v2-api/auth/push/reject", {
    request_id: requestID,
  });
}

// --- WebAuthn (Re-)Auth-Codec --------------------------------------
//
// go-webauthn liefert Challenges und IDs als Base64-Strings (urlsafe).
// navigator.credentials.get erwartet aber BufferSources. Wir mirrorn
// die in passkey.ts vorhandene Konvertierung mit b64urlToBytes/encode.

interface PublicKeyCredentialRequestOptionsJSON {
  challenge: string;
  timeout?: number;
  rpId?: string;
  allowCredentials?: Array<{
    type: string;
    id: string;
    transports?: string[];
  }>;
  userVerification?: UserVerificationRequirement;
}

function decodeRequestOptions(
  json: PublicKeyCredentialRequestOptionsJSON,
): PublicKeyCredentialRequestOptions {
  return {
    challenge: b64urlToBytes(json.challenge),
    timeout: json.timeout,
    rpId: json.rpId,
    userVerification: json.userVerification,
    allowCredentials: (json.allowCredentials ?? []).map((c) => ({
      type: c.type as PublicKeyCredentialType,
      id: b64urlToBytes(c.id),
      transports: c.transports as AuthenticatorTransport[] | undefined,
    })),
  };
}

interface AssertionJSON {
  id: string;
  rawId: string;
  type: string;
  response: {
    clientDataJSON: string;
    authenticatorData: string;
    signature: string;
    userHandle?: string;
  };
}

function encodeAssertion(cred: PublicKeyCredential): AssertionJSON {
  const r = cred.response as AuthenticatorAssertionResponse;
  return {
    id: cred.id,
    rawId: bytesToB64url(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bytesToB64url(r.clientDataJSON),
      authenticatorData: bytesToB64url(r.authenticatorData),
      signature: bytesToB64url(r.signature),
      userHandle: r.userHandle ? bytesToB64url(r.userHandle) : undefined,
    },
  };
}
